import type { AdminClient, IntegratorSummary } from '@apx/admin-sdk';
import { navigate } from './nav.js';
import { GAME_IDS, prettyGame } from './games.js';

interface PaletteItem {
  kind: 'route' | 'integrator' | 'player' | 'round';
  label: string;
  hint?: string;
  href: string;
}

export interface PaletteDeps {
  client: () => AdminClient | null;
  integrators: () => IntegratorSummary[];
  currentTenantId: () => string | null;
  /** Gates the `superadmin` routes below. The palette is otherwise unfiltered
   *  by permission, so identity-gated entries must opt in explicitly rather
   *  than inherit that. */
  isSuperAdmin: () => boolean;
}

/**
 * Per-game deep links, generated for EVERY game in the catalog.
 *
 * These were hand-written for greedyking alone, so the palette silently had no
 * entry for any game added since — you could jump straight to greedyking's RTP
 * tab but had to click through /games for the other four.
 *
 * `jackpot` is listed only where a pool exists (greedyking, sicbo); the rest of
 * these tabs are on every game's whitelist in `game-detail.ts`. A link to a tab
 * a game does not support is not fatal — `renderGameDetailPage` falls back to
 * that game's first supported tab — but advertising one would be misleading.
 */
const GAME_TABS: ReadonlyArray<{ tab: string; label: string }> = [
  { tab: 'timing', label: 'Phase timing' },
  { tab: 'rtp', label: 'RTP & Payouts' },
  { tab: 'table', label: 'Table limits' },
  { tab: 'big-winner', label: 'Big winner' },
  { tab: 'leaderboard', label: 'Leaderboard' },
  { tab: 'rules', label: 'Rules' },
  { tab: 'inspector', label: 'Live inspector' },
];

/** Games with a jackpot pool — mirrors KNOWN_JACKPOT_GAMES in admin-api. */
const JACKPOT_GAMES = new Set(['greedyking', 'sicbo']);

const GAME_ROUTES: Array<{ label: string; href: string; hint: string }> = GAME_IDS.flatMap(
  (gameId) => {
    const tabs = JACKPOT_GAMES.has(gameId)
      ? [...GAME_TABS, { tab: 'jackpot', label: 'Jackpot' }]
      : GAME_TABS;
    return tabs.map(({ tab, label }) => ({
      label: `${prettyGame(gameId)} · ${label}`,
      href: `/games/${gameId}/${tab}`,
      hint: gameId,
    }));
  },
);

const SIDEBAR_ROUTES: Array<{
  label: string;
  href: string;
  hint: string;
  /** Offered only to a global super_admin — see PaletteDeps.isSuperAdmin. */
  superadmin?: true;
}> = [
  { label: 'Home', href: '/home', hint: 'overview' },
  { label: 'Integrators', href: '/integrators', hint: 'platform' },
  { label: 'Admin users', href: '/admin-users', hint: 'platform' },
  { label: 'System health', href: '/system-health', hint: 'platform' },
  { label: 'Redis monitoring', href: '/redis-monitoring', hint: 'platform', superadmin: true },
  { label: 'Players', href: '/players', hint: "integrator" },
  { label: 'Game catalog', href: '/game-catalog', hint: "integrator" },
  { label: 'API keys', href: '/api-keys', hint: "integrator" },
  { label: 'Webhooks', href: '/webhooks', hint: "integrator" },
  { label: 'Games', href: '/games', hint: "integrator" },
  ...GAME_ROUTES,
  { label: 'Live inspector · all tenants', href: '/inspector-all', hint: 'live ops' },
  { label: 'Round ops', href: '/round-ops', hint: 'live ops' },
  { label: 'Round explorer', href: '/rounds-explorer', hint: 'live ops' },
  { label: 'Integrator call log', href: '/wallet-ledger', hint: 'live ops' },
  { label: 'Wallet drift', href: '/wallet-drift', hint: 'reconcile' },
  { label: 'GGR / RTP reports', href: '/reports', hint: 'reporting' },
  { label: 'Pending approvals', href: '/approvals', hint: 'audit' },
  { label: 'Bulk config', href: '/bulk-config', hint: 'all integrators' },
  { label: 'Audit log', href: '/audit', hint: 'audit' },
  { label: 'Audit chains', href: '/audit-chains', hint: 'tamper-evident hashes' },
  { label: 'Audit anchors', href: '/audit-anchors', hint: 'daily Merkle roots' },
];

let active = 0;
let items: PaletteItem[] = [];
let depsRef: PaletteDeps | null = null;

function el<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function fuzzyScore(needle: string, hay: string): number {
  if (!needle) return 1;
  const n = needle.toLowerCase();
  const h = hay.toLowerCase();
  if (h.includes(n)) return 100 - (h.indexOf(n) || 0);
  let ni = 0;
  for (let i = 0; i < h.length && ni < n.length; i += 1) {
    if (h[i] === n[ni]) ni += 1;
  }
  return ni === n.length ? 1 : 0;
}

async function buildItems(query: string): Promise<PaletteItem[]> {
  if (!depsRef) return [];
  const routes: PaletteItem[] = SIDEBAR_ROUTES.filter(
    (r) => !!r.label && (!r.superadmin || depsRef!.isSuperAdmin()),
  ).map((r) => ({
    kind: 'route',
    label: r.label,
    href: r.href,
    hint: r.hint,
  }));

  const tenantItems: PaletteItem[] = depsRef.integrators().map((t) => ({
    kind: "integrator",
    label: `${t.slug} — ${t.name}`,
    href: `/integrators/${t.id}`,
    hint: t.status,
  }));

  const all: PaletteItem[] = [...routes, ...tenantItems];

  // Player/round lookup needs a integrator + client; only kick when query looks like
  // an identifier (>= 4 chars, alphanumeric/uuid-ish).
  const client = depsRef.client();
  const tid = depsRef.currentTenantId();
  if (client && tid && query.length >= 4 && /^[a-z0-9-]+$/i.test(query)) {
    try {
      const { players } = await client.listPlayers(tid, { search: query, limit: 8 });
      for (const p of players) {
        all.push({
          kind: 'player',
          label: p.username ?? `(anon ${p.id.slice(0, 8)})`,
          href: `/players/${p.id}`,
          hint: p.banned ? 'banned' : p.isAnonymous ? 'anon' : 'active',
        });
      }
    } catch {
      // ignore — players lookup is best-effort
    }
  }

  if (!query) return all.slice(0, 30);
  const scored = all
    .map((it) => ({ it, s: fuzzyScore(query, `${it.label} ${it.hint ?? ''}`) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, 30)
    .map((x) => x.it);
  return scored;
}

function render(): void {
  const resultsEl = el<HTMLElement>('palette-results');
  if (!resultsEl) return;
  if (items.length === 0) {
    resultsEl.innerHTML = '<div class="pr-item"><span class="pr-label muted">No matches</span></div>';
    return;
  }
  resultsEl.innerHTML = items
    .map(
      (it, i) => `<div class="pr-item ${i === active ? 'active' : ''}" data-i="${i}">
        <span class="pr-kind">${it.kind}</span>
        <span class="pr-label">${escapeHtml(it.label)}</span>
        ${it.hint ? `<span class="pr-hint">${escapeHtml(it.hint)}</span>` : ''}
      </div>`,
    )
    .join('');
  resultsEl.querySelectorAll<HTMLElement>('.pr-item[data-i]').forEach((node) => {
    node.addEventListener('mouseenter', () => {
      active = Number(node.dataset.i);
      highlightActive();
    });
    node.addEventListener('click', () => {
      activate();
    });
  });
}

function highlightActive(): void {
  document.querySelectorAll<HTMLElement>('.pr-item').forEach((node, i) => {
    node.classList.toggle('active', i === active);
  });
}

function open(): void {
  const palette = el<HTMLElement>('palette');
  const input = el<HTMLInputElement>('palette-input');
  if (!palette || !input) return;
  palette.classList.remove('hidden');
  input.value = '';
  active = 0;
  void refresh('');
  input.focus();
}

function close(): void {
  const palette = el<HTMLElement>('palette');
  if (palette) palette.classList.add('hidden');
}

function activate(): void {
  const target = items[active];
  if (!target) return;
  close();
  navigate(target.href.replace(/^#/, ''));
}

async function refresh(query: string): Promise<void> {
  items = await buildItems(query);
  if (active >= items.length) active = 0;
  render();
}

export function installPalette(deps: PaletteDeps): void {
  depsRef = deps;
  const input = el<HTMLInputElement>('palette-input');
  const palette = el<HTMLElement>('palette');
  if (!input || !palette) return;

  let debounce: number | undefined;
  input.addEventListener('input', () => {
    const value = input.value;
    if (debounce !== undefined) window.clearTimeout(debounce);
    debounce = window.setTimeout(() => void refresh(value), 120);
  });

  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      active = Math.min(active + 1, items.length - 1);
      highlightActive();
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      active = Math.max(active - 1, 0);
      highlightActive();
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      activate();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      close();
    }
  });

  // Click outside card closes.
  palette.addEventListener('click', (ev) => {
    if (ev.target === palette) close();
  });

  window.addEventListener('keydown', (ev) => {
    if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'k') {
      ev.preventDefault();
      if (palette.classList.contains('hidden')) open();
      else close();
    }
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
